import type { BooruPost } from "./booru-api"
import { useSettingsStore } from "./settings-store"
import { useSourceStore } from "./source-store"

/**
 * Determines the navigation mode for an album based on settings precedence:
 * 1. Threshold (highest priority) - if album size meets threshold
 * 2. Per-source override - user's per-source setting
 * 3. Source default - source's preferred mode
 * 4. Global setting (lowest priority) - user's global preference
 */
export function getNavigationMode(album: BooruPost, source: string): 'nested' | 'modal' {
  // Erome has no purpose-built nested album view (components/album-grid-view.tsx
  // has no erome branch at all — its generic `album.albumImages` fallback only
  // works by accident for the subset of erome posts eagerly fetched at search
  // time, mislabels every item "image" even when it's a video, and can't lazy-
  // load a placeholder post the way the modal already does). Route it to the
  // modal unconditionally rather than through the size/threshold logic below.
  //
  // That threshold logic also has a real gap this masked: albumSize falls back
  // to 0 for a not-yet-fetched erome placeholder (its true count is genuinely
  // unknown, not zero), and both threshold comparisons below require
  // albumSize > 0 to fire — so an unmeasured post silently skipped the
  // threshold entirely and fell through to a DIFFERENT setting tier than a
  // measured post from the very same search would use. That's why clicking
  // two erome results back to back could open two different UIs: one openly
  // eagerly fetched (known size, hit the threshold, went 'nested') and one
  // still a placeholder (unknown size read as 0, skipped the threshold,
  // landed on whatever 'modal'/'nested' the source/global setting picked).
  if (source === 'erome') return 'modal'

  const { nestedGridMode, nestedGridThreshold, perSourceNestedMode } = useSettingsStore.getState()
  const { getSource } = useSourceStore.getState()

  // Calculate album size
  const albumSize = album.albumImages?.length || album.albumMediaCount || 0
  
  // 1. Threshold check (highest priority)
  if (nestedGridThreshold > 0) {
    if (albumSize >= nestedGridThreshold) {
      console.log(`[v0] Navigation mode: nested (threshold ${nestedGridThreshold}, album size ${albumSize})`)
      return 'nested'
    }
    if (albumSize < nestedGridThreshold && albumSize > 0) {
      console.log(`[v0] Navigation mode: modal (below threshold ${nestedGridThreshold}, album size ${albumSize})`)
      return 'modal'
    }
  }
  
  // 2. Per-source override
  if (perSourceNestedMode[source] !== undefined) {
    const mode = perSourceNestedMode[source] ? 'nested' : 'modal'
    console.log(`[v0] Navigation mode: ${mode} (per-source override for ${source})`)
    return mode
  }
  
  // 3. Source default
  const sourceConfig = getSource(source)
  if (sourceConfig?.defaultNestedMode !== undefined) {
    const mode = sourceConfig.defaultNestedMode ? 'nested' : 'modal'
    console.log(`[v0] Navigation mode: ${mode} (source default for ${source})`)
    return mode
  }
  
  // 4. Global setting (lowest priority)
  const mode = nestedGridMode ? 'nested' : 'modal'
  console.log(`[v0] Navigation mode: ${mode} (global setting)`)
  return mode
}
